/**
 * vendorCartLines.js
 * ----------------------------------------------------------------------------
 * ONE way a line the server built goes into the cart it belongs to.
 *
 * Two server answers carry cart lines, both built by the server's
 * services/orders/vendorCartLines.js, so a line is priced and checked there
 * and never here:
 *
 *   GET  /api/orders/:orderId/reorder   "Order again" (utils/reorderIntoCart.js)
 *   POST /api/catalog/cart-line         "Add to cart" on a /shop grid card
 *                                       (utils/cartRouting.js)
 *
 * Both name the vendor the same way: `vertical`, `vendorOwnerId` (the owner's
 * account) and `vendorDocumentId` (the vendor's own record), and this module
 * maps them onto the cart slice actions the storefronts already dispatch, so
 * a line added from either is stored exactly as a line added by hand:
 *
 *   restaurant  restaurantCart, keyed by `vendorOwnerId`
 *   pharmacy    healthcareCart, keyed by `vendorOwnerId`
 *   brand       brandCart, keyed by `vendorDocumentId` (the brand)
 *   shop        retailerWholesalerCart, keyed by `vendorDocumentId` (the shop)
 *   lusuku      the in-house cart (one list, no vendor key)
 * ----------------------------------------------------------------------------
 */
import {
  addToCart as addToLusukuCart,
  incrementCartItem as incrementLusukuCartItem,
} from "../redux/cartRedux";
import { addToCart as addToRestaurantCart } from "../redux/restaurantCartSlice";
import { addToCart as addToPharmacyCart } from "../redux/healthcareCartSlice";
import { addToCart as addToBrandCart } from "../redux/brandCartSlice";
import { addToCart as addToShopCart } from "../redux/retailerWholesalerCartSlice";

export const CART_VERTICALS = Object.freeze({
  RESTAURANT: "restaurant",
  SHOP: "shop",
  BRAND: "brand",
  PHARMACY: "pharmacy",
  LUSUKU: "lusuku",
});

/* Which server-named identity each website cart is keyed by. */
const CART_KEY_FIELD = Object.freeze({
  [CART_VERTICALS.RESTAURANT]: "vendorOwnerId",
  [CART_VERTICALS.PHARMACY]: "vendorOwnerId",
  [CART_VERTICALS.BRAND]: "vendorDocumentId",
  [CART_VERTICALS.SHOP]: "vendorDocumentId",
});

/**
 * The key this vertical's website cart is stored under, read off a server
 * answer, or null (Lusuku has none). `vendorId` is read only when an answer
 * predates the named identities.
 */
export const vendorCartKey = (answer) => {
  const field = CART_KEY_FIELD[answer?.vertical];
  return field ? answer[field] || answer.vendorId || null : null;
};

export const lineQuantityOf = (line) => Math.max(1, Math.round(Number(line?.quantity) || 1));
const optionsOf = (line) => (Array.isArray(line?.options) ? line.options : []);

/* One builder per vertical: the actions that add ONE server line to the cart
   stored under `cartKey`. */
const LINE_ADDERS = {
  [CART_VERTICALS.RESTAURANT]: (cartKey, line, { outletId }) => [
    addToRestaurantCart({
      restaurantId: cartKey,
      product: { ...line.product, options: optionsOf(line) },
      quantity: lineQuantityOf(line),
      options: optionsOf(line),
      // One branch per restaurant cart, named outright (null is main).
      outletId: outletId || null,
    }),
  ],
  [CART_VERTICALS.PHARMACY]: (cartKey, line, { outletId }) => [
    addToPharmacyCart({
      vendorId: cartKey,
      product: line.product,
      quantity: lineQuantityOf(line),
      options: optionsOf(line),
      outletId: outletId || null,
    }),
  ],
  [CART_VERTICALS.BRAND]: (cartKey, line) => {
    const selectedSpecs = line.selectedSpecs || {};
    const specPriceDelta = Number(line.specPriceDelta) || 0;
    return [
      addToBrandCart({
        brandId: cartKey,
        // The branch rides on the product (`outletId`), as the brand pages add it.
        product: { ...line.product, selectedSpecs, specPriceDelta },
        quantity: lineQuantityOf(line),
        selectedSpecs,
        specPriceDelta,
      }),
    ];
  },
  [CART_VERTICALS.SHOP]: (cartKey, line) => [
    addToShopCart({ vendorId: cartKey, product: line.product, quantity: lineQuantityOf(line) }),
  ],
  /* The in-house cart adds one unit per action, so a quantity of 3 is one add
     and two increments. */
  [CART_VERTICALS.LUSUKU]: (_cartKey, line) => [
    addToLusukuCart({ item: line.product }),
    ...Array.from({ length: lineQuantityOf(line) - 1 }, () => incrementLusukuCartItem({ item: line.product })),
  ],
};

/** Does this vertical have a website cart? */
export const hasVendorCart = (vertical) => Boolean(LINE_ADDERS[vertical]);

/**
 * The actions that add one server line to its vendor's cart, in dispatch
 * order. Pure. An unknown vertical, a line without a product, or a vendor
 * cart with no key adds nothing.
 *
 * @param {string} vertical  CART_VERTICALS
 * @param {string|null} cartKey  vendorCartKey's answer (null for Lusuku)
 * @param {{ product: object, quantity?: number, options?: object[],
 *           selectedSpecs?: object, specPriceDelta?: number }} line
 * @param {{ outletId?: string|null }} [where]
 * @returns {object[]}
 */
export const vendorLineAddActions = (vertical, cartKey, line, { outletId = null } = {}) => {
  const adder = LINE_ADDERS[vertical];
  if (!adder || !line?.product) return [];
  if (vertical !== CART_VERTICALS.LUSUKU && !cartKey) return [];
  return adder(cartKey, line, { outletId });
};
